import { useSyncExternalStore } from 'react';
import { Platform } from 'react-native';
import type {
  AgapellaAccessState,
  AgapellaSubscriptionOffer,
  ContentAccess,
} from '../types/agapellaSubscription';
import { canStartTrial, priceLine } from '../screens/subscription/agapellaSubscription';
import { refreshAgapellaAccess } from './agapellaAccess';
import { analytics } from './analytics';

/**
 * CORE-1776 — одна точка, где решается «закрыто ли это подпиской Agapēlla»
 * (страница CORE-662, раздел 3). Сервер решает (CORE-1769/1770), клиент только
 * объясняет: бейдж заранее и окно вместо поломки. Экраны своих проверок не
 * пишут — спрашивают здесь.
 */

/** Код отказа на входе в эфир, комнату, чат эфира (CORE-1769). */
export const AGAPELLA_SUBSCRIPTION_REQUIRED = 'agapella_subscription_required';

/** Что открывают: от этого зависят слова в окне. */
export type AgapellaContentKind = 'live' | 'room' | 'practice' | 'recording' | 'ai';

interface GatedItem {
  access?: ContentAccess | null;
  /**
   * CORE-1780 — сервер сам скрыл ссылки воспроизведения этому человеку
   * (CORE-1770, `PlaybackLocked`). Знает раньше и точнее, чем состояние доступа
   * на клиенте: пустая ссылка при нём — «закрыто», а не «записи нет».
   */
  playbackLocked?: boolean | null;
}

/**
 * Бейдж «Входит в Agapēlla». Флаг `agapella_subscription` выключен — нет,
 * даже если сервер прислал `included`: тогда он и так пускает всех.
 */
export function showsAgapellaBadge(item: GatedItem | null | undefined, state: AgapellaAccessState | null): boolean {
  return item?.access === 'included' && state?.enabled === true;
}

/**
 * Закрыто ли это для меня. Сомнение — в пользу открытого: пока состояние не
 * загрузилось, не запираем, решит ответ сервера на входе. Владелец и ведущий
 * не запираются (сервер их пускает всегда). Ссылку воспроизведения сервер уже
 * отдал — значит, этому человеку можно, что бы ни думало состояние: отозванный
 * посреди практики доступ даёт ей доиграть (CORE-1780), закроет следующий заход.
 */
export function isAgapellaLocked(
  item: GatedItem | null | undefined,
  state: AgapellaAccessState | null,
  opts: { isOwner?: boolean; hasPlayback?: boolean } = {},
): boolean {
  if (opts.isOwner || opts.hasPlayback === true) return false;
  if (item?.playbackLocked === true) return true;
  if (!showsAgapellaBadge(item, state)) return false;
  return state?.hasAccess === false;
}

/**
 * Отказ ли это по подписке. Ошибка REST (`handleResponse` кладёт статус и код
 * на тело) или событие `Error` хаба чата эфира — у него кода нет статуса.
 */
export function isAgapellaSubscriptionRequired(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { status?: number; code?: unknown; Code?: unknown };
  const code = e.code ?? e.Code;
  if (code !== AGAPELLA_SUBSCRIPTION_REQUIRED) return false;
  return e.status == null || e.status === 403;
}

/**
 * Что предлагает окно (раздел 4.2):
 * - `guest` — не вошёл: сперва войти;
 * - `trial` — пробный доступ ещё не был: он бесплатный, предлагаем везде;
 * - `pay` — оплата здесь разрешена (CORE-1535) и подписка продаётся;
 * - `store` — CORE-1773: вне РФ на iOS и Android подписка продаётся в сторе;
 * - `info` — об оплате говорить нельзя (iOS в РФ, стор недоступен) или не продаётся.
 */
export type AgapellaPaywallMode = 'guest' | 'trial' | 'pay' | 'store' | 'info';

export function agapellaPaywallMode(
  isAuthenticated: boolean,
  state: AgapellaAccessState | null,
  canPay: boolean | null,
  offer: AgapellaSubscriptionOffer | null,
  canStore: boolean | null = false,
): AgapellaPaywallMode {
  if (!isAuthenticated) return 'guest';
  if (canStartTrial(state)) return 'trial';
  if (canPay === true && offer?.forSale) return 'pay';
  if (canStore === true) return 'store';
  return 'info';
}

// Комната в интерфейсе — тоже Live-сессия (словарь CORE-853); `room` отличается
// только в аналитике.
const SUBJECT: Record<AgapellaContentKind, string> = {
  live: 'Эта Live-сессия',
  room: 'Эта Live-сессия',
  practice: 'Эта практика',
  recording: 'Эта запись',
  // CORE-1781 — бесплатные сообщения Агапелле кончились (CORE-1771).
  ai: 'Разговор без ограничений',
};

export interface AgapellaPaywallCopy {
  title: string;
  lines: string[];
  primary: string;
  secondary: string | null;
}

/** Тексты окна. Про цену и оплату — только в `pay`. */
export function agapellaPaywallCopy(
  kind: AgapellaContentKind,
  mode: AgapellaPaywallMode,
  offer: AgapellaSubscriptionOffer | null,
): AgapellaPaywallCopy {
  const subject = SUBJECT[kind];
  if (mode === 'guest') {
    return {
      title: `${subject} входит в Agapēlla`,
      lines: ['Войди, и я покажу, как её открыть'],
      primary: 'Войти',
      secondary: 'Не сейчас',
    };
  }
  if (mode === 'trial') {
    return {
      title: `${subject} входит в Agapēlla`,
      lines: ['Открой 7 дней полного доступа — без оплаты и без автоматического списания'],
      primary: 'Начать 7 дней',
      secondary: 'Не сейчас',
    };
  }
  if (mode === 'pay') {
    const price = offer ? priceLine(offer) : null;
    return {
      title: `${subject} входит в подписку Agapēlla`,
      lines: price ? [price] : [],
      primary: 'Оформить подписку',
      secondary: 'Не сейчас',
    };
  }
  if (mode === 'store') {
    return {
      title: `${subject} входит в подписку Agapēlla`,
      lines: ['Оформить подписку можно прямо в приложении'],
      primary: 'Оформить подписку',
      secondary: 'Не сейчас',
    };
  }
  return {
    title: `${subject} входит в подписку Agapēlla`,
    lines: ['Если подписка уже есть, она откроется под тем же аккаунтом'],
    primary: 'Понятно',
    secondary: null,
  };
}

/** Доступ по состоянию есть, а сервер всё равно отказал (рассинхрон). */
export const AGAPELLA_ACCESS_MISMATCH_LINE = 'Не получилось открыть. Попробуй ещё раз';

// ─── Окно: один запрос на приложение ─────────────────────────────────────────

export interface AgapellaPaywallRequest {
  kind: AgapellaContentKind;
  /** Откуда открыли — для аналитики: `live_player`, `room_join`… */
  surface: string;
  /** Доступ открылся из окна (trial) — повторить то, что открывали. */
  onUnlocked?: () => void;
  /**
   * CORE-1779 — окно закрыли, ничего не открыв («Не сейчас», «Понятно»,
   * крестик). Комната по нему уходит назад: стоять перед запертой дверью незачем.
   */
  onDismiss?: () => void;
}

let request: AgapellaPaywallRequest | null = null;
const listeners = new Set<() => void>();

function emit(next: AgapellaPaywallRequest | null): void {
  request = next;
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => { listeners.delete(l); };
};
const snapshot = () => request;

/** Показать окно. Уже открытое заменяется: последний запрос — то, что человек нажал сейчас. */
export function openAgapellaPaywall(next: AgapellaPaywallRequest): void {
  emit(next);
}

export function closeAgapellaPaywall(): void {
  emit(null);
}

/** Открытый запрос вне React — для того, кто решает, не мешать ли окну. */
export function getAgapellaPaywallRequest(): AgapellaPaywallRequest | null {
  return request;
}

/** Для `AgapellaPaywallHost` и тех, кому нельзя всплывать поверх окна. */
export function useAgapellaPaywallRequest(): AgapellaPaywallRequest | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

// ─── Реакция на отказ ────────────────────────────────────────────────────────

export type AgapellaDenialOutcome = 'retried' | 'paywall' | 'mismatch';

/**
 * Сервер отказал по подписке. Сперва перечитать доступ: купил на другом
 * устройстве или оплата только что подтвердилась — повторить один раз молча.
 * Доступа нет — окно. Доступ есть, а повтор снова отказан (или повторять
 * нечего) — `mismatch`:
 * экран пишет `AGAPELLA_ACCESS_MISMATCH_LINE`, окно не показываем.
 *
 * Ретраи экрана (спиннер эфира, CORE-942 в комнате) на этот отказ не
 * срабатывают — повтор только отсюда.
 */
export async function handleAgapellaDenial(
  req: AgapellaPaywallRequest & { retry?: () => Promise<unknown> },
): Promise<AgapellaDenialOutcome> {
  const { retry, ...paywall } = req;
  const next = await refreshAgapellaAccess();
  if (next?.hasAccess) {
    if (!retry) return mismatch(paywall, next);
    try {
      await retry();
      return 'retried';
    } catch (e) {
      if (!isAgapellaSubscriptionRequired(e)) throw e;
      return mismatch(paywall, next);
    }
  }
  openAgapellaPaywall({ ...paywall, onUnlocked: paywall.onUnlocked ?? (retry ? () => { void retry(); } : undefined) });
  return 'paywall';
}

function mismatch(req: AgapellaPaywallRequest, state: AgapellaAccessState): AgapellaDenialOutcome {
  analytics.track('agapella_access_mismatch', {
    surface: req.surface, kind: req.kind, status: state.status, platform: Platform.OS,
  });
  return 'mismatch';
}

/** Только для тестов. */
export function __resetAgapellaPaywallForTests(): void {
  request = null;
  listeners.clear();
}
